<template>
  <div class="checkbox-triggers-tab">
    <div class="tab-toolbar">
      <b-button size="sm" variant="primary" @click="openAddModal">
        <i class="fa fa-plus"></i>
        {{ $t('pluginFiscalTriggers.add_trigger') }}
      </b-button>
      <b-button size="sm" variant="outline-secondary" @click="refresh" :disabled="isLoading" class="ml-2">
        <i class="fa" :class="isLoading ? 'fa-spinner fa-spin' : 'fa-refresh'"></i>
        {{ $t('pluginCashiers.refresh') }}
      </b-button>
    </div>

    <div class="tab-info text-muted small mb-3">
      <i class="fa fa-info-circle mr-1"></i>
      {{ $t('pluginFiscalTriggers.hint') }}
    </div>

    <div v-if="isLoading && !hasLoaded" class="tab-loading text-center text-muted py-4">
      <i class="fa fa-spinner fa-spin mr-1"></i>
      {{ $t('common.loading') }}
    </div>

    <div v-else class="tab-content">
      <table class="table table-sm table-bordered table-hover">
        <thead>
          <tr>
            <th style="width: 50px">ID</th>
            <th>{{ $t('pluginFiscalTriggers.th_status') }}</th>
            <th style="width: 140px">{{ $t('pluginFiscalTriggers.th_receipt_type') }}</th>
            <th style="width: 90px">{{ $t('pluginFiscalTriggers.th_active') }}</th>
            <th style="width: 100px">{{ $t('pluginFiscalTriggers.th_actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in items" :key="item.id">
            <td>{{ item.id }}</td>
            <td>
              <span v-if="item.status_color" class="status-dot" :style="{ backgroundColor: item.status_color }"></span>
              {{ item.order_status_title }}
            </td>
            <td>
              <span class="badge" :class="item.receipt_type === 'sale' ? 'badge-success' : 'badge-warning'">
                {{ item.receipt_type_label }}
              </span>
            </td>
            <td class="text-center">
              <toggle-button
                :value="item.is_active"
                :margin="0"
                :width="40"
                :height="20"
                color="#94D974"
                @change="toggleActive(item)"
              />
            </td>
            <td class="actions-cell">
              <b-button variant="link" size="sm" @click="openEditModal(item)" :title="$t('common.edit')">
                <i class="fa fa-edit"></i>
              </b-button>
              <b-button variant="link" size="sm" class="text-danger" @click="confirmDelete(item)" :title="$t('common.delete')">
                <i class="fa fa-trash"></i>
              </b-button>
            </td>
          </tr>
          <tr v-if="!items.length && hasLoaded">
            <td colspan="5" class="text-center text-muted py-3">
              {{ $t('pluginFiscalTriggers.empty_list') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Add/Edit Modal -->
    <div v-if="showModal" class="trigger-modal-overlay" @click.self="closeModal">
      <div class="trigger-modal" @click.stop>
        <div class="trigger-modal__header">
          <h5>{{ isEditMode ? $t('pluginFiscalTriggers.modal_edit') : $t('pluginFiscalTriggers.modal_new') }}</h5>
          <button type="button" class="close-btn" @click="closeModal">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="trigger-modal__body">
          <b-form-group :label="$t('pluginFiscalTriggers.th_status')" label-cols-sm="5" label-size="sm" class="mb-3">
            <v-select
              v-model="modalForm.order_status_id"
              :options="statusesList"
              :reduce="i => i.id"
              label="title"
              :placeholder="$t('pluginFiscalTriggers.status_ph')"
              :class="{ 'is-invalid': modalErrors.order_status_id }"
            >
              <template #option="option">
                <span v-if="option.color" class="status-dot" :style="{ backgroundColor: option.color }"></span>
                {{ option.title }}
              </template>
              <template #selected-option="option">
                <span v-if="option.color" class="status-dot" :style="{ backgroundColor: option.color }"></span>
                {{ option.title }}
              </template>
            </v-select>
            <div v-if="modalErrors.order_status_id" class="invalid-feedback d-block">
              {{ modalErrors.order_status_id }}
            </div>
          </b-form-group>

          <b-form-group :label="$t('pluginFiscalTriggers.th_receipt_type')" label-cols-sm="5" label-size="sm" class="mb-3">
            <v-select
              v-model="modalForm.receipt_type"
              :options="receiptTypes"
              :reduce="i => i.value"
              label="label"
              :placeholder="$t('pluginFiscalTriggers.type_ph')"
              :class="{ 'is-invalid': modalErrors.receipt_type }"
            />
            <div v-if="modalErrors.receipt_type" class="invalid-feedback d-block">
              {{ modalErrors.receipt_type }}
            </div>
          </b-form-group>

          <b-form-group :label="$t('pluginFiscalTriggers.th_active')" label-cols-sm="5" label-size="sm" class="mb-3">
            <toggle-button
              v-model="modalForm.is_active"
              :margin="0"
              :width="40"
              :height="20"
              color="#94D974"
            />
          </b-form-group>
        </div>
        <div class="trigger-modal__footer">
          <b-button variant="secondary" size="sm" @click="closeModal" class="mr-2">
            {{ $t('popup.cancel') }}
          </b-button>
          <b-button variant="primary" size="sm" @click="saveModal" :disabled="isSaving">
            <i v-if="isSaving" class="fa fa-spinner fa-spin mr-1"></i>
            {{ isEditMode ? $t('common.save') : $t('common.add') }}
          </b-button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteConfirm" class="trigger-modal-overlay" @click.self="showDeleteConfirm = false">
      <div class="trigger-modal trigger-modal--small" @click.stop>
        <div class="trigger-modal__header">
          <h5>{{ $t('popup.remove_confirm') }}</h5>
          <button type="button" class="close-btn" @click="showDeleteConfirm = false">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="trigger-modal__body text-center">
          <p>{{ $t('pluginFiscalTriggers.delete_confirm') }} <strong>{{ deletingItem && deletingItem.order_status_title }}</strong>?</p>
        </div>
        <div class="trigger-modal__footer justify-content-center">
          <b-button variant="secondary" size="sm" @click="showDeleteConfirm = false" class="mr-2">
            {{ $t('popup.cancel') }}
          </b-button>
          <b-button variant="danger" size="sm" @click="deleteItem" :disabled="isDeleting">
            <i v-if="isDeleting" class="fa fa-spinner fa-spin mr-1"></i>
            {{ $t('common.delete') }}
          </b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'FiscalizationTriggersTab',
  props: {
    active: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: false,
      hasLoaded: false,
      isSaving: false,
      isDeleting: false,
      items: [],
      statusesList: [],
      receiptTypes: [
        { value: 'sale', label: this.$t('pluginReceipts.type_sale') },
        { value: 'refund', label: this.$t('pluginReceipts.type_refund') },
      ],
      showModal: false,
      isEditMode: false,
      editingItem: null,
      modalForm: {
        order_status_id: null,
        receipt_type: 'sale',
        is_active: true,
      },
      modalErrors: {},
      showDeleteConfirm: false,
      deletingItem: null,
    };
  },
  computed: {
    ...mapGetters({
      fiscalizationTriggers: 'checkbox/fiscalizationTriggers',
      triggerStatuses: 'checkbox/triggerStatuses',
    }),
  },
  watch: {
    active: {
      immediate: true,
      handler(val) {
        if (val && !this.hasLoaded) {
          this.loadData();
        }
      },
    },
    fiscalizationTriggers: {
      immediate: true,
      handler(data) {
        this.items = [];
        if (data && Array.isArray(data)) {
          data.forEach(item => {
            const receiptTypeObj = this.receiptTypes.find(t => t.value === item.receipt_type);
            this.items.push({
              id: item.id,
              order_status_id: item.order_status_id,
              order_status_title: item.order_status ? item.order_status.title : '—',
              status_color: item.order_status ? item.order_status.color : null,
              receipt_type: item.receipt_type,
              receipt_type_label: receiptTypeObj ? receiptTypeObj.label : item.receipt_type,
              is_active: item.is_active,
              _raw: item,
            });
          });
        }
      },
    },
    triggerStatuses: {
      immediate: true,
      handler(data) {
        this.statusesList = data || [];
      },
    },
  },
  methods: {
    async loadData() {
      this.isLoading = true;
      try {
        await Promise.all([
          this.getFiscalizationTriggers(),
          this.getTriggerStatuses(),
        ]);
        this.hasLoaded = true;
      } catch (e) {
        this.$toasted.error(this.$t('pluginCheckbox.err_load'), { duration: 3000 });
      } finally {
        this.isLoading = false;
      }
    },
    refresh() {
      this.loadData();
    },
    openAddModal() {
      this.isEditMode = false;
      this.editingItem = null;
      this.modalForm = {
        order_status_id: null,
        receipt_type: 'sale',
        is_active: true,
      };
      this.modalErrors = {};
      this.showModal = true;
    },
    openEditModal(item) {
      this.isEditMode = true;
      this.editingItem = item;
      this.modalForm = {
        order_status_id: item.order_status_id,
        receipt_type: item.receipt_type,
        is_active: item.is_active,
      };
      this.modalErrors = {};
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.editingItem = null;
      this.modalErrors = {};
    },
    validateModal() {
      this.modalErrors = {};
      if (!this.modalForm.order_status_id) {
        this.modalErrors.order_status_id = this.$t('pluginFiscalTriggers.status_required');
      }
      if (!this.modalForm.receipt_type) {
        this.modalErrors.receipt_type = this.$t('pluginFiscalTriggers.type_required');
      }
      return Object.keys(this.modalErrors).length === 0;
    },
    async saveModal() {
      if (!this.validateModal()) {
        return;
      }
      this.isSaving = true;
      try {
        const payload = { ...this.modalForm };
        if (this.isEditMode) {
          const res = await this.updateTrigger({ id: this.editingItem.id, data: payload });
          if (res.status) {
            this.$toasted.success(this.$t('pluginFiscalTriggers.trigger_updated'), { duration: 3000 });
            this.closeModal();
            this.loadData();
          } else {
            this.$toasted.error((res.data && res.data.message) || this.$t('pluginCheckbox.err_update'), { duration: 3000 });
          }
        } else {
          const res = await this.createTrigger(payload);
          if (res.status) {
            this.$toasted.success(this.$t('pluginFiscalTriggers.trigger_created'), { duration: 3000 });
            this.closeModal();
            this.loadData();
          } else {
            this.$toasted.error((res.data && res.data.message) || this.$t('pluginCheckbox.err_create'), { duration: 3000 });
          }
        }
      } catch (e) {
        this.$toasted.error(this.$t('pluginCheckbox.err_save'), { duration: 3000 });
      } finally {
        this.isSaving = false;
      }
    },
    async toggleActive(item) {
      try {
        const res = await this.updateTrigger({
          id: item.id,
          data: { is_active: !item.is_active },
        });
        if (res.status) {
          this.$toasted.success(
            item.is_active ? this.$t('pluginFiscalTriggers.trigger_deactivated') : this.$t('pluginFiscalTriggers.trigger_activated'),
            { duration: 2000 }
          );
          this.loadData();
        } else {
          this.$toasted.error((res.data && res.data.message) || this.$t('pluginFiscalTriggers.err_status_change'), { duration: 3000 });
        }
      } catch (e) {
        this.$toasted.error(this.$t('pluginFiscalTriggers.err_status_change'), { duration: 3000 });
      }
    },
    confirmDelete(item) {
      this.deletingItem = item;
      this.showDeleteConfirm = true;
    },
    async deleteItem() {
      if (!this.deletingItem) return;
      this.isDeleting = true;
      try {
        const res = await this.deleteTrigger(this.deletingItem.id);
        if (res.status) {
          this.$toasted.success(this.$t('pluginFiscalTriggers.trigger_deleted'), { duration: 3000 });
          this.showDeleteConfirm = false;
          this.deletingItem = null;
          this.loadData();
        } else {
          this.$toasted.error((res.data && res.data.message) || this.$t('pluginCheckbox.err_delete'), { duration: 3000 });
        }
      } catch (e) {
        this.$toasted.error(this.$t('pluginCheckbox.err_delete'), { duration: 3000 });
      } finally {
        this.isDeleting = false;
      }
    },
    ...mapActions({
      getFiscalizationTriggers: 'checkbox/getFiscalizationTriggers',
      getTriggerStatuses: 'checkbox/getTriggerStatuses',
      createTrigger: 'checkbox/createTrigger',
      updateTrigger: 'checkbox/updateTrigger',
      deleteTrigger: 'checkbox/deleteTrigger',
    }),
  },
};
</script>

<style lang="scss" scoped>
.checkbox-triggers-tab {
  padding: 15px;
  min-height: 300px;
}

.tab-toolbar {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.tab-info {
  padding: 8px 12px;
  background: #f0f7ff;
  border-radius: 4px;
  border-left: 3px solid #007bff;
}

.status-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 6px;
  vertical-align: middle;
}

.tab-content {
  .table {
    margin-bottom: 0;
    font-size: 13px;

    th {
      background: #f8f9fa;
      font-weight: 600;
      white-space: nowrap;
    }

    td {
      vertical-align: middle;
    }
  }

  .actions-cell {
    white-space: nowrap;
    text-align: center;

    .btn-link {
      padding: 2px 6px;
    }
  }

  .badge-success {
    background-color: #28a745;
  }

  .badge-warning {
    background-color: #ffc107;
    color: #212529;
  }
}

.trigger-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1060;
}

.trigger-modal {
  background: #fff;
  border-radius: 8px;
  width: 500px;
  max-width: 90vw;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);

  &--small {
    width: 400px;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid #dee2e6;
    background: #f8f9fa;

    h5 {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
    }

    .close-btn {
      border: none;
      background: none;
      font-size: 18px;
      color: #6c757d;
      cursor: pointer;
      padding: 0;
      line-height: 1;

      &:hover {
        color: #343a40;
      }
    }
  }

  &__body {
    padding: 16px;
    overflow-y: auto;
    max-height: calc(90vh - 120px);
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    padding: 12px 16px;
    border-top: 1px solid #dee2e6;
    background: #f8f9fa;
  }
}

::v-deep .form-group {
  label {
    text-align: right;
    padding-right: 1rem;
    font-size: 13px;
  }
}

::v-deep .v-select {
  &.is-invalid .vs__dropdown-toggle {
    border-color: #dc3545;
  }

  .vs__dropdown-toggle {
    min-height: 31px;
  }
}
</style>

